import { useContext, useRef, useState } from "react";
import { api } from "../../../api";
import DeliveryHoursSettings from "../../../components/DeliveryHoursSettings";
import { OverflowFrame } from "../../../externalComponents";
import { PageHeading } from "../../../externalComponents/PageHeading";
import { SettingsSidebar } from "../../../externalComponents/SettingsSidebar";
import { CustomerContext } from "../../../providers";

export default function DeliveryHoursPage() {
  const { customer } = useContext(CustomerContext);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const deliveryHoursRef = useRef(null);

  const handleSave = async (deliveryHoursData) => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      await api.customer.update(customer.id, {
        customer: deliveryHoursData,
      });

      setSaveMessage({
        type: "success",
        text: "Delivery hours saved successfully!",
      });
    } catch (error) {
      console.error("Error saving delivery hours:", error);
      setSaveMessage({
        type: "error",
        text: "Failed to save delivery hours. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrimaryAction = async () => {
    if (deliveryHoursRef.current) {
      await deliveryHoursRef.current.save();
    }
  };

  return (
    <SettingsSidebar>
      <PageHeading
        title="Delivery Hours"
        primaryAction="Save settings"
        handlePrimaryAction={handlePrimaryAction}
        primaryActionLoading={isSaving}
      />
      <OverflowFrame>
        <div className="my-5 max-w-[800px]">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="text-blue-900 font-semibold mb-2 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              How delivery hours works
            </h3>
            <p className="text-blue-800 text-sm leading-relaxed">
              Delivery hours define the time window during which your <strong>WhatsApp campaigns</strong> can be sent to contacts.
              If a campaign is scheduled outside these hours, it will be <strong>automatically postponed</strong> to the next available time slot within your delivery window.
              This ensures your messages are sent at appropriate times based on your timezone.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <h3 className="text-amber-900 font-semibold mb-2 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Important: Automations are not affected
            </h3>
            <p className="text-amber-800 text-sm leading-relaxed">
              These delivery hours settings <strong>only apply to manual campaigns</strong>.
              Automated workflows (abandoned cart, back-in-stock alerts, order tracking, etc.) will continue to send messages immediately when triggered, regardless of these time restrictions.
            </p>
          </div>

          {saveMessage && (
            <div
              className={`p-4 mb-4 rounded-lg border ${saveMessage.type === "success" ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}
            >
              {saveMessage.text}
            </div>
          )}

          <DeliveryHoursSettings
            ref={deliveryHoursRef}
            customer={customer}
            onSave={handleSave}
          />
        </div>
      </OverflowFrame>
    </SettingsSidebar>
  );
}
